import React, { useState } from 'react';
import { Container, Alert, TextField, Button, Typography, Grid, MenuItem, Box, Autocomplete, Divider, InputAdornment } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/pt-br';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NumericFormat } from 'react-number-format';
import CurrencyInput from '../commons/CurrencyInput';
import { AssetClass, AssetInstrumentType, getAssetClasses, getAssetInstrumentTypesByClass, getDecimalQuantityFromAssetClass, getDecimalPriceFromAssetClass } from '../../utils/utils';
import { getCurrencyTypes } from '../../utils/Currency';
import walletApi from '../../api/wallet';
import { getErrorMessage } from '../../utils/errors';

const OperationsForm = ({ onCreate }) => {
  const theme = useTheme();
  const [assetName, setAssetName] = useState('');
  const [assetClass, setAssetClass] = useState('');
  const [instrumentType, setInstrumentType] = useState('');
  const [quantity, setQuantity] = useState('');
  const [fees, setFees] = useState(0);
  const [unitValue, setUnitValue] = useState(0);
  const [currency, setCurrency] = useState('BRL');
  const [operationType, setTransactionType] = useState('buy');
  const [operationDate, setOperationDate] = useState(dayjs().locale('pt-br').format('DD/MM/YYYY'));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rate, setRate] = useState('');
  const [maturity, setMaturity] = useState(null);
  const [liquidity, setLiquidity] = useState('DAILY');
  const [valor, setValor] = useState(0);
  const types = getAssetClasses();
  const instrumentOptions = getAssetInstrumentTypesByClass(assetClass);
  const Currencytypes = getCurrencyTypes();
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const operationAccent = operationType === 'buy' ? theme.palette.success.main : theme.palette.error.main;
  const operationBackground = alpha(operationAccent, theme.palette.mode === 'dark' ? 0.25 : 0.12);
  const operationBorder = alpha(operationAccent, theme.palette.mode === 'dark' ? 0.7 : 0.6);
  const isInstrumentRequired = instrumentOptions.length > 0;
  const isFixedIncome = assetClass === AssetClass.FixedIncome;
  const isFixedIncomeETF = instrumentType === AssetInstrumentType.FixedIncomeETF;
  const isTraditionalFixedIncome = isFixedIncome && !isFixedIncomeETF;
  const isSubmitDisabled = !assetName
    || !assetClass
    || (isInstrumentRequired && !instrumentType)
    || (!isFixedIncome && (!quantity || unitValue <= 0))
    || (isTraditionalFixedIncome && (valor <= 0 || !rate || !maturity))
    || (isFixedIncomeETF && (!quantity || unitValue <= 0))
    || !operationDate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullDate = `${dayjs(operationDate, 'DD/MM/YYYY')
      .tz('America/Sao_Paulo')
      .format('YYYY-MM-DDTHH:mm:ssZ')}`;

    try {
      const payload = {
        "Asset": {
          "name": assetName,
          "class": assetClass,
          "type": instrumentType,
        },
        "OperationDate": fullDate,
        "Quantity": isTraditionalFixedIncome ? String(valor) : quantity,
        "Fees": String(fees),
        "UnitValue": isTraditionalFixedIncome ? "1" : String(unitValue),
        "Currency": currency,
        "OperationType": operationType
      };

      if (isTraditionalFixedIncome) {
        payload.Rate = rate;
        payload.Maturity = maturity ? dayjs(maturity).format('YYYY-MM-DDTHH:mm:ssZ') : null;
        payload.Liquidity = liquidity;
      }

      await walletApi.post('/operations', payload);

      setSuccess('Operacão criada com sucesso!');
      setAssetName('');
      setAssetClass('');
      setInstrumentType('');
      setQuantity('');
      setFees(0);
      setUnitValue(0);
      setTransactionType('buy');
      setCurrency('BRL');
      setOperationDate(dayjs().locale('pt-br').format('DD/MM/YYYY'));
      setRate('');
      setMaturity(null);
      setLiquidity('DAILY');
      setValor(0);
      if (onCreate) onCreate();
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao criar a operação.'));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 2,
          boxShadow: theme.palette.mode === 'dark' ? 2 : 3,
          backgroundColor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
          mb: 4,
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Nova Operação
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle1" sx={{ mt: 1, color: 'text.secondary' }}>
            Dados da operação
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <DatePicker
              label="Data da Operação"
              value={dayjs(operationDate, 'DD/MM/YYYY')}
              onChange={(newValue) => setOperationDate(newValue.format('DD/MM/YYYY'))}
              slotProps={{ textField: { fullWidth: true, variant: 'outlined', margin: 'normal', size: 'small' } }}
            />
          </LocalizationProvider>
          <TextField
            label="Nome do Ativo"
            placeholder="Ex.: BTC, MSFT, WEGE3, KNRI11"
            variant="outlined"
            margin="normal"
            
            fullWidth
            required
            value={assetName}
            onChange={(e) => setAssetName(e.target.value.toUpperCase())}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={types}
                getOptionLabel={(option) => option.label}
                value={types.find((typeOption) => typeOption.value === assetClass) || null}
                onChange={(event, newValue) => {
                  const nextClass = newValue ? newValue.value : '';
                  const nextInstrumentOptions = getAssetInstrumentTypesByClass(nextClass);
                  const nextInstrumentType = nextInstrumentOptions.length === 1
                    ? nextInstrumentOptions[0].value
                    : '';
                  setAssetClass(nextClass);
                  setInstrumentType(nextInstrumentType);
                  if (nextClass === AssetClass.Crypto) {
                    setCurrency('USD');
                  } else if (nextClass) {
                    setCurrency('BRL');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Classe do Ativo"
                    variant="outlined"
                    margin="normal"
                    
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Tipo do Ativo"
                variant="outlined"
                margin="normal"
                
                fullWidth
                required={isInstrumentRequired}
                value={instrumentType}
                onChange={(e) => setInstrumentType(e.target.value)}
                disabled={instrumentOptions.length === 0}
              >
                {instrumentOptions.map((typeOption) => (
                  <MenuItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Moeda"
                variant="outlined"
                margin="normal"
                
                fullWidth
                required
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {Currencytypes.map((typeOption) => (
                  <MenuItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            Valores
          </Typography>
          {isTraditionalFixedIncome ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CurrencyInput
                    label="Valor Investido"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    value={valor}
                    onChange={setValor}
                    currency={currency}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CurrencyInput
                    label="Taxas"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    value={fees}
                    onChange={setFees}
                    currency={currency}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                Dados do Contrato
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Taxa"
                    placeholder="Ex.: 110% CDI, IPCA + 6%"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DatePicker
                      label="Vencimento"
                      value={maturity}
                      onChange={(newValue) => setMaturity(newValue)}
                      slotProps={{ textField: { fullWidth: true, variant: 'outlined', margin: 'normal', required: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="Liquidez"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    value={liquidity}
                    onChange={(e) => setLiquidity(e.target.value)}
                  >
                    <MenuItem value="DAILY">Diária</MenuItem>
                    <MenuItem value="MATURITY">No Vencimento</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <NumericFormat
                  label="Quantidade"
                  placeholder="Ex.: 100, 1.5"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  customInput={TextField}
                  value={quantity}
                  onValueChange={(values) => setQuantity(values.value)}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={getDecimalQuantityFromAssetClass(assetClass)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumericFormat
                  label="Valor Unitário"
                  placeholder="Ex.: 28,50"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  customInput={TextField}
                  value={unitValue || ''}
                  onValueChange={(values) => setUnitValue(values.floatValue || 0)}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={getDecimalPriceFromAssetClass(assetClass)}
                  allowNegative={false}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency === 'USD' ? '$' : 'R$'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CurrencyInput
                  label="Taxas"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  value={fees}
                  onChange={setFees}
                  currency={currency}
                />
              </Grid>
            </Grid>
          )}
          <TextField
            select
            label="Tipo de Transação"
            variant="outlined"
            margin="normal"
            
            fullWidth
            required
            value={operationType}
            onChange={(e) => setTransactionType(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: operationBackground,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: operationAccent,
                  borderWidth: '2px',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: operationAccent,
                  borderWidth: '2px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: operationBorder,
                },
              },
              '& .MuiInputLabel-root': {
                color: operationAccent,
                '&.Mui-focused': {
                  color: operationAccent,
                },
              },
            }}
          >
            <MenuItem value="buy" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>
              📈 Compra
            </MenuItem>
            <MenuItem value="sell" sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
              📉 Venda
            </MenuItem>
          </TextField>
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" size="large" disabled={isSubmitDisabled}>
                Cadastrar
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => {
                  setAssetName('');
                  setAssetClass('');
                  setInstrumentType('');
                  setQuantity('');
                  setFees(0);
                  setUnitValue(0);
                  setTransactionType('buy');
                  setCurrency('BRL');
                  setOperationDate(dayjs().locale('pt-br').format('DD/MM/YYYY'));
                  setRate('');
                  setMaturity(null);
                  setLiquidity('DAILY');
                  setValor(0);
                }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default OperationsForm;